import PricingPlans from '../../../components/PricingPlans.js';
import { PRODUCT_NAME } from '../../../constants/brand';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Container, Typography, Box, Button, Card, CardContent, Divider, CircularProgress, LinearProgress, Tabs, Tab, Paper, TextField, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Alert
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentIcon from '@mui/icons-material/Payment';
import TimelineIcon from '@mui/icons-material/Timeline';
import KeyIcon from '@mui/icons-material/Key';
import AccessCodesSection from './AccessCodesSection.js';
import SubscriptionInvoices from './SubscriptionInvoices.js';
import StripeElements from '../../../components/payments/StripeElements';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { supabase } from '../../../supabaseClient.js';
import useAlert from '../../../alert/useAlert.js';
import { useSelector, shallowEqual } from 'react-redux';
import moment from 'moment';
import { billingPresentation, hasExistingBillingSubscription, reconcileBillingStatus, paidPlanAction, featuresForCadence, freePlanAction } from './billingStatus.js';
import Loader from '../../../loader.js';
import { SUBSCRIPTION_PLANS } from '../../../constants/subscriptionPlans.js';
import { authedFetch } from '../../../utils/backendFetch.js';
import { activateFreePlan } from '../../../utils/freePlanActivation.js';
import { useWalletBalance, walletCredits } from '../../../creditWallet/WalletBalanceContext.js';
import BuyCreditsButton from '../../../creditWallet/BuyCreditsDialog.js';
import { loadRechargeSubscription, getRechargeStatus } from '../../../utils/rechargeEligibility';
import { PHASE45_COMMS_ENABLED } from '../../../constants/phase45Comms.js';
import { onDocumentReturn } from '../../../utils/onDocumentReturn.js';


const MAX_REFRESH_ATTEMPTS = 5;
const REFRESH_INTERVAL_MS = 2000;

export const WalletBalanceSummary = ({ subscription, subscriptionLoading = false, subscriptionUnavailable = false, onViewPlans }) => {
  const { wallet, loading } = useWalletBalance();
  const credits = walletCredits(wallet);
  const outOfCredits = credits !== null && credits <= 0;
  const rechargeStatus = subscriptionLoading ? 'loading' : subscriptionUnavailable ? 'unavailable' : getRechargeStatus(subscription);
  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3, borderLeft: `4px solid ${outOfCredits ? '#d32f2f' : '#29C0BB'}` }}>
      <Typography variant="body2" color="text.secondary">Available credit balance</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        {loading && credits === null ? 'Loading…' : credits === null ? 'Unavailable' : `${credits} credits`}
      </Typography>
      {outOfCredits && (
        <Typography color="error" sx={{ mt: 0.5 }}>
          {subscriptionLoading ? 'Out of credits — checking your plan…' : rechargeStatus === 'eligible'
            ? 'Out of credits — buy a credit pack below or upgrade your plan'
            : rechargeStatus === 'unavailable' ? 'Out of credits — could not verify your paid plan'
            : 'Out of credits — join an active paid plan to buy credits'}
        </Typography>
      )}
      {/* Phase 6 "Buy credits" entry (D-014 packs) — gated by
          PHASE45_COMMS_ENABLED; renders nothing while the flag is off. */}
      {PHASE45_COMMS_ENABLED && (
        <Box sx={{ mt: 1 }}>
          <BuyCreditsButton subscription={subscription} subscriptionLoading={subscriptionLoading} subscriptionUnavailable={subscriptionUnavailable} onViewPlans={onViewPlans} />
        </Box>
      )}
    </Paper>
  );
};

// Tab Panel Component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`subscription-tabpanel-${index}`}
      aria-labelledby={`subscription-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Helper function for tab props
function a11yProps(index) {
  return {
    id: `subscription-tab-${index}`,
    'aria-controls': `subscription-tabpanel-${index}`,
  };
}

const StyledTab = styled((props) => <Tab {...props} />)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.pxToRem(15),
  marginRight: theme.spacing(1),
  '&.Mui-selected': {
    color: '#29C0BB',
    fontWeight: theme.typography.fontWeightMedium,
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'rgba(41, 192, 187, 0.32)',
  },
}));

/* eslint-disable react/prop-types */
// Current Subscription Details Component
export const CurrentSubscription = ({ subscription, onUpgrade, loading, handleSubscriptionComplete, onManagePayment, onRefresh, unavailable, errorMessage }) => {
  const { setAlert } = useAlert();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  
  const handleCancelSubscription = async () => {
    setIsCanceling(true);
    try {
      const response = await authedFetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/cancel-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscriptionId: subscription.billing?.id || subscription.current_stripe_subscription_id || subscription.stripe_subscription_id,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }

      setAlert('Auto-renewal is off. Your subscription credits remain usable until they expire. Any outstanding invoice remains payable.', 'success');
      setCancelDialogOpen(false);
      handleSubscriptionComplete({ shouldPoll: false });
    } catch (error) {
      setAlert('Failed to cancel subscription: ' + error.message, 'error');
    } finally {
      setIsCanceling(false);
    }
  };

  const handleCancelClick = () => {
    setCancelDialogOpen(true);
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Loader />
      </Box>
    )
  }
  
  if (unavailable) return <Alert severity="error">{errorMessage || 'Could not confirm your current subscription. Please try again.'} <Button onClick={onRefresh}>Retry</Button></Alert>;
  if (!subscription) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>No active subscription</Typography>
        <Button 
          variant="contained"
          onClick={onUpgrade}
          sx={{ 
            backgroundColor: '#29C0BB', 
            '&:hover': { backgroundColor: '#1D8A87' } 
          }}
        >
          Subscribe Now
        </Button>
      </Box>
    );
  }
  
  const view = billingPresentation(subscription);
  const tier = subscription.billing?.tier || subscription.plan_name || 'free';
  const planName = tier.charAt(0).toUpperCase() + tier.slice(1);
  const isActivePlan = view.active;
  const isFreePlan = subscription.plan_name === 'free';
  const endDate = isFreePlan ? subscription.trial?.expires_at : subscription.billing?.period_end || subscription.subscription_end_date;
  const startDate = isFreePlan ? subscription.trial?.granted_at : subscription.billing?.period_start || subscription.subscription_start_date;
  const nextBillingDate = view.active && !view.canceling ? endDate : null;
  const isPaidPlan = planName !== 'Free';

  return (
    <>
      <Paper data-tour="subscription-current-plan" elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
              {planName} Plan
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CheckCircleIcon color={isActivePlan ? "success" : "disabled"} sx={{ mr: 1 }} />
              <Typography variant="body1" color={isActivePlan ? "text.primary" : "text.secondary"}>
                {view.label}
              </Typography>
            </Box>
          </Box>
          <Button 
            variant="outlined"
            color="primary"
            onClick={onUpgrade}
            disabled={!isFreePlan && !view.canChange}
            sx={{ borderColor: '#29C0BB', color: '#29C0BB' }}
          >
            {isFreePlan ? 'Upgrade Plan' : view.ended ? 'Subscribe Again' : 'Change Plan'}
          </Button>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        {view.message && <Alert severity={view.severity} sx={{ mb: 2 }}>{view.message}</Alert>}
        {isPaidPlan && <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Button variant="outlined" onClick={onManagePayment}>Manage payment card</Button>
          {subscription.billing?.invoice_url && <Button variant="contained" component="a"
            href={subscription.billing.invoice_url} target="_blank" rel="noopener noreferrer">{view.ended ? 'View outstanding invoice' : 'Pay outstanding invoice'}</Button>}
        </Box>}
        {subscription.billing?.commitment?.cadence === 'annual' && <Alert severity="info" sx={{ mb: 2 }}>
          {subscription.billing.commitment.end
            ? `Annual plan is billed monthly. The 12-month commitment ends on ${moment(subscription.billing.commitment.end).format('MMM DD, YYYY')}.`
            : 'Your annual commitment dates need confirmation. Contact support before canceling or switching to Monthly.'}
        </Alert>}
        {isFreePlan && subscription.trial?.status === 'expired' && <Alert severity="info" sx={{ mb: 2 }}>
          Your trial credits have expired. You can subscribe to a paid plan. No additional trial credits will be issued.
        </Alert>}
        {isFreePlan && subscription.trial?.status === 'unverified' && <Alert severity="warning" sx={{ mb: 2 }}>
          Your trial credit dates could not be confirmed. You can still subscribe to a paid plan.
        </Alert>}
        <Grid container spacing={2}>
          {isFreePlan ? (
            <>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" color="text.secondary">Trial Credits Granted On</Typography>
                <Typography variant="body1">{startDate ? moment(startDate).format('MMM DD, YYYY') : 'Unavailable'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" color="text.secondary">Trial Credits Expire On</Typography>
                <Typography variant="body1">{endDate ? moment(endDate).format('MMM DD, YYYY') : 'Unavailable'}</Typography>
              </Grid>
            </>
          ) : (
            <>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" color="text.secondary">Start Date</Typography>
                <Typography variant="body1">{startDate ? moment(startDate).format('MMM DD, YYYY') : 'Unavailable'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" color="text.secondary">End Date</Typography>
                <Typography variant="body1">{endDate ? moment(endDate).format('MMM DD, YYYY') : 'Unavailable'}</Typography>
              </Grid>
              {nextBillingDate && isActivePlan && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2" color="text.secondary">Next Billing Date</Typography>
                  <Typography variant="body1">{moment(nextBillingDate).format('MMM DD, YYYY')}</Typography>
                </Grid>
              )}
            </>
          )}
        </Grid>
        
        {isPaidPlan && subscription.billing?.commitment?.locked && <Button variant="outlined" disabled sx={{ mt: 3 }}>Cancel Subscription</Button>}
        {isPaidPlan && view.canCancel && (
          <Box sx={{ mt: 3 }}>
            {
              !view.canceling ? (
                <Button 
                  variant="outlined" 
                  color="error" 
                  onClick={handleCancelClick}
                  sx={{ borderColor: '#f44336' }}
                  disabled={isCanceling}
                >
                  {isCanceling ? <CircularProgress size={20} /> : 'Cancel Subscription'}
                </Button>
              )
              :
              <Typography variant="body2" color="text.secondary">
                {view.active ? 'You won’t be charged for the next billing period. Your subscription credits remain usable until they expire.'
                  : 'Auto-renewal is off. Any outstanding invoice remains payable.'}
              </Typography>
            }
          </Box>
        )}
      </Paper>

      {/* Cancel Subscription Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="cancel-dialog-title" sx={{ color: '#f44336', fontWeight: 'bold' }}>
          Cancel Subscription
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-dialog-description" sx={{ mb: 2 }}>
            Are you sure you want to cancel your <strong>{planName} Plan</strong> subscription?
          </DialogContentText>
          <Box sx={{ p: 2, backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7', mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#856404' }}>
              ⚠️ Important Information:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#856404' }}>
              <li>
                <Typography variant="body2">
                  {view.active ? 'Your paid period continues until ' : 'Cancellation is requested for the current period end: '}<strong>{endDate ? moment(endDate).format('MMM DD, YYYY') : 'Unavailable'}</strong>
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  {view.active ? 'Your existing paid period is retained.' : 'Payment recovery rules may end an overdue subscription earlier.'}
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  No further subscription renewals are scheduled. Outstanding invoices remain payable.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  You can reactivate your subscription at any time before it expires
                </Typography>
              </li>
            </ul>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleCancelDialogClose} 
            variant="outlined"
            sx={{ borderColor: '#29C0BB', color: '#29C0BB' }}
            disabled={isCanceling}
          >
            Keep Subscription
          </Button>
          <Button 
            onClick={handleCancelSubscription} 
            variant="contained"
            color="error"
            disabled={isCanceling}
            sx={{ ml: 2 }}
          >
            {isCanceling ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Canceling...
              </>
            ) : (
              'Yes, Cancel Subscription'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/* eslint-disable react/prop-types */
// Usage Statistics Component
const UsageStatistics = ({ subscription, loading }) => {
  const { wallet, loading: walletLoading } = useWalletBalance();

  // Per-activity credit usage for the current cycle, from the get_credit_usage
  // SECURITY DEFINER function (the wallet/ledger tables are locked down; this
  // read-only function reads them as its owner, scoped to auth.uid(), and
  // returns only aggregates). Falls back to the allotment-bucket estimate if
  // unavailable.
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    let active = true;
    supabase.rpc('get_credit_usage')
      .then(({ data, error }) => {
        if (!active) return;
        if (!error && data && typeof data.total_used_credits === 'number') setUsage(data);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  if (loading || walletLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Loader />
      </Box>
    );
  }
  if (!subscription) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>No subscription data available</Typography>
      </Box>
    );
  }

  // Credit-wallet usage. The pre-credit-wallet seconds model
  // (transcription_seconds_used/limit) is no longer updated by the backend, so
  // usage is measured in credits. "Used this cycle" comes from the wallet's
  // allotment bucket: the plan grants `monthlyCredits` into it each cycle and
  // every scribe activity draws it down first, so allotment - allotment_halves/2
  // is the plan credits consumed this cycle. Recharge / rolled-over credits sit
  // in a separate bucket (shown as the total wallet balance) and are excluded
  // here. When the get-credit-usage function responds, the exact per-activity
  // ledger total replaces this estimate and a breakdown is shown below.
  const tierKey = subscription.credit_tier || subscription.plan_name || 'free';
  const plan = Object.values(SUBSCRIPTION_PLANS).find((p) => p.tier === tierKey)
    || Object.values(SUBSCRIPTION_PLANS).find((p) => p.tier === 'free');
  const allotment = Number(plan?.monthlyCredits) || 0;
  const allotmentRemaining = (wallet && wallet.allotment_halves != null)
    ? Math.max(0, Number(wallet.allotment_halves) / 2)
    : allotment;
  // Prefer the exact per-activity total from the ledger (via the Edge Function)
  // when available; otherwise fall back to the allotment-bucket estimate.
  const ledgerUsed = usage && typeof usage.total_used_credits === 'number' ? usage.total_used_credits : null;
  const used = ledgerUsed != null ? ledgerUsed : Math.max(0, allotment - allotmentRemaining);
  const usagePercentage = allotment > 0 ? Math.min((used / allotment) * 100, 100) : 0;
  const totalBalance = Math.max(0, walletCredits(wallet) ?? 0);

  // Per-activity breakdown for the current cycle (only shown when the function
  // responded). Labels/order fixed; zero-usage categories are hidden.
  const CATEGORY_LABELS = {
    live_transcription: 'Live transcription',
    recorded_transcription: 'Recorded transcription',
    report: 'Reports',
    client_connection: 'Client connections',
    other: 'Other',
  };
  const breakdown = usage && usage.by_category
    ? Object.keys(CATEGORY_LABELS)
        .map((key) => ({
          key,
          label: CATEGORY_LABELS[key],
          credits: Number(usage.by_category[key]?.credits) || 0,
          count: Number(usage.by_category[key]?.count) || 0,
        }))
        .filter((row) => row.credits > 0 || row.count > 0)
    : [];

  const cycleEnd = subscription.next_billing_date || (wallet && wallet.allotment_expires_at) || null;
  const fmtDate = (d) => {
    try { return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch (e) { return null; }
  };
  const fmtCredits = (c) => `${Number(c).toLocaleString(undefined, { maximumFractionDigits: 1 })} credits`;

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Credit Usage{plan && plan.name ? ` — ${plan.name} plan` : ''}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {cycleEnd ? `This billing cycle · renews ${fmtDate(cycleEnd)}` : 'This billing cycle'}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">Used</Typography>
          <Typography variant="body2" fontWeight="bold">
            {fmtCredits(used)} ({Math.round(usagePercentage)}%)
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={usagePercentage}
          sx={{
            height: 10,
            borderRadius: 5,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              backgroundColor: usagePercentage > 90 ? '#f44336' : usagePercentage > 75 ? '#ff9800' : '#29C0BB'
            }
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">Allotment remaining</Typography>
        <Typography variant="body2" fontWeight="bold">{fmtCredits(allotmentRemaining)}</Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">Total wallet balance</Typography>
        <Typography variant="body2" fontWeight="bold">{fmtCredits(totalBalance)}</Typography>
      </Box>

      {breakdown.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            By activity this cycle
          </Typography>
          {breakdown.map((row) => (
            <Box key={row.key} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {row.label}{row.count ? ` (${row.count})` : ''}
              </Typography>
              <Typography variant="body2" fontWeight="bold">{fmtCredits(row.credits)}</Typography>
            </Box>
          ))}
        </>
      )}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">Plan allotment / cycle</Typography>
        <Typography variant="body2" fontWeight="bold">{fmtCredits(allotment)}</Typography>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        {breakdown.length > 0
          ? `"Used" is the total credits spent on ${PRODUCT_NAME} activity this cycle. Recharge top-ups and rolled-over credits are counted in the total wallet balance, not here.`
          : '"Used" is the plan credits consumed this cycle (your monthly allotment minus what remains in it). Recharge top-ups and rolled-over credits are counted in the total wallet balance, not here.'}
      </Typography>
    </Paper>
  );
};

/* eslint-disable react/prop-types */
// Payment Methods Component
export const PaymentMethods = ({ userDetails, loading, onUpdated }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [clientSecret, setClientSecret] = useState('');
  const [defaultMethodId, setDefaultMethodId] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [savingMethod, setSavingMethod] = useState(false);
  const [renewalRemoval, setRenewalRemoval] = useState(null);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [removingMethod, setRemovingMethod] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { setAlert } = useAlert();
  
  const fetchPaymentMethods = useCallback(async () => {
      if (!userDetails) return;
      
      try {
        const response = await authedFetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/get-payment-methods`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch payment methods');
        }
        
        const data = await response.json();
        setPaymentMethods(data.paymentMethods || []);
        setDefaultMethodId(data.defaultPaymentMethodId || null);
        setRenewalRemoval(data.renewalRemoval || null);
        setLoadError(false);
      } catch (error) {
        setLoadError(true);
        setPaymentMethods([]);
        setRenewalRemoval(null);
      } finally {
        setIsLoading(false);
      }
    }, [userDetails]);
  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  useEffect(() => { setPendingRemoval(null); }, [userDetails?.id]);

  const selectPaymentCard = async (paymentMethodId) => {
    setSavingMethod(true);
    try {
      const response = await authedFetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/set-subscription-payment-method`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentMethodId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not change your subscription payment card');
      setClientSecret('');
      setAlert('Subscription payment card updated. This does not confirm payment of an outstanding invoice.', 'success');
      await fetchPaymentMethods();
      if (onUpdated) await onUpdated();
    } catch (error) {
      await fetchPaymentMethods();
      throw error;
    } finally { setSavingMethod(false); }
  };
  
  const handleUpdatePaymentMethod = async () => {
    try {
      const response = await authedFetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/create-setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: userDetails.name, email: userDetails.email }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to initialize payment update');
      }
      
      const { clientSecret } = await response.json();
      setClientSecret(clientSecret);
    } catch (error) {
      setAlert('Failed to initialize payment update: ' + error.message, 'error');
    }
  };
  
  const handleRemovePaymentMethod = async (paymentMethodId, subscriptionId = null) => {
    if (removingMethod) return;
    setRemovingMethod(true);
    try {
      const response = await authedFetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/remove-payment-method`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentMethodId,
          ...(subscriptionId ? { disableAutoRenewal: true, subscriptionId } : {}),
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to remove payment method');
      }
      if (result.success !== true) throw new Error('Card removal could not be confirmed. Refresh your billing details.');
      setPendingRemoval(null);
      setAlert(result.renewalDisabled
        ? 'Payment method removed and automatic renewal disabled. Your subscription remains active until the end of the paid billing period.'
        : 'Payment method removed successfully', 'success');
    } catch (error) {
      setPendingRemoval(null);
      setAlert('Failed to remove payment method: ' + error.message, 'error');
    } finally {
      // Stripe changes can partially succeed. Reconcile both cards and billing
      // on errors too; never remove a card or end access optimistically.
      await fetchPaymentMethods();
      try { if (onUpdated) await onUpdated(); }
      catch (_) { setAlert('Refresh Subscription Details to confirm the latest billing status.', 'warning'); }
      setRemovingMethod(false);
    }
  };
  
  if (loading || isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Loader />
      </Box>
    )
  }
  
  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#29C0BB',
      }
    },
    linkAuthenticationElement: {
      mode: 'disabled'
    },
    wallet: {
      applePay: 'never',
      googlePay: 'never',
      link: 'never'
    }
  };
    
  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Payment Methods
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 2 }}>Choose the card for subscription renewals and automatic payment retries. After saving a new card, check that it is marked as your subscription payment card. If card verification redirects you, select Use for subscription when you return.</Typography>
      {loadError ? <Alert severity="error">Could not load payment methods. <Button onClick={fetchPaymentMethods}>Retry</Button></Alert> : paymentMethods.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>No payment methods found</Typography>
          <Button 
            variant="contained"
            startIcon={<CreditCardIcon />}
            onClick={handleUpdatePaymentMethod}
            sx={{ 
              backgroundColor: '#29C0BB', 
              '&:hover': { backgroundColor: '#1D8A87' } 
            }}
          >
            Add Payment Method
          </Button>
        </Box>
      ) : (
        <>
          {paymentMethods.map((method) => (
            <Card key={method.id} sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Grid container alignItems="center">
                  <Grid size={8}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CreditCardIcon sx={{ mr: 1 }} />
                      <Box>
                        <Typography variant="body1">
                          {method.card.brand.charAt(0).toUpperCase() + method.card.brand.slice(1)} •••• {method.card.last4}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Expires {method.card.exp_month}/{method.card.exp_year}
                          {method.id === defaultMethodId && ' · Subscription payment card'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid size={4} sx={{ textAlign: 'right' }}>
                    {method.id !== defaultMethodId && <Button size="small" variant="outlined" disabled={savingMethod || removingMethod}
                      onClick={() => selectPaymentCard(method.id).catch(error => setAlert(error.message, 'error'))}>
                      Use for subscription
                    </Button>}
                    <Button 
                      size="small" 
                      color="error" 
                      disabled={savingMethod || removingMethod || (method.id === defaultMethodId &&
                        !(renewalRemoval?.paymentMethodId === method.id && renewalRemoval?.subscriptionId))}
                      onClick={() => method.id === defaultMethodId
                        ? setPendingRemoval({ paymentMethodId: method.id, subscriptionId: renewalRemoval.subscriptionId })
                        : handleRemovePaymentMethod(method.id)}
                    >
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
          
          <Box sx={{ mt: 2 }}>
            <Button 
              variant="outlined"
              startIcon={<CreditCardIcon />}
              disabled={savingMethod || removingMethod}
              onClick={handleUpdatePaymentMethod}
              sx={{ borderColor: '#29C0BB', color: '#29C0BB' }}
            >
              Update Payment Method
            </Button>
          </Box>
        </>
      )}
      
      <Dialog open={Boolean(pendingRemoval)} onClose={() => !removingMethod && setPendingRemoval(null)}
        fullWidth maxWidth="sm" aria-labelledby="remove-payment-method-title">
        <DialogTitle id="remove-payment-method-title">Remove Payment Method?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Removing your saved credit card will disable automatic renewal of your subscription. Your current subscription will remain active until the end of the billing period. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={removingMethod} onClick={() => setPendingRemoval(null)}>Cancel</Button>
          <Button color="error" disabled={removingMethod || !pendingRemoval} onClick={() => pendingRemoval && handleRemovePaymentMethod(pendingRemoval.paymentMethodId, pendingRemoval.subscriptionId)}>
            {removingMethod ? 'Removing…' : 'Remove card and disable renewal'}
          </Button>
        </DialogActions>
      </Dialog>

      {clientSecret && (
        <Box sx={{ mt: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Add New Payment Method
          </Typography>
          <StripeElements options={options}>
            <SetupForm 
              onSuccess={selectPaymentCard}
              onCancel={() => setClientSecret('')}
            />
          </StripeElements>
        </Box>
      )}
    </Paper>
  );
};


/* eslint-disable react/prop-types */
// Subscription History Component
const SubscriptionHistory = ({ userDetails, loading, subscriptionHistory, onRefresh, error }) => {
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success.main';
      case 'canceled':
      case 'cancelled':
        return 'warning.main';
      case 'incomplete':
      case 'incomplete_expired':
        return 'error.main';
      case 'past_due':
        return 'warning.main';
      case 'unpaid':
        return 'error.main';
      default:
        return 'text.secondary';
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'canceled':
      case 'cancelled':
        return 'Cancelled';
      case 'incomplete':
        return 'Incomplete';
      case 'incomplete_expired':
        return 'Expired';
      case 'past_due':
        return 'Past Due';
      case 'unpaid':
        return 'Unpaid';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };
  
  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };
  
  const formatDate = (timestamp) => {
    return moment.unix(timestamp).format('MMM DD, YYYY');
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Loader />
      </Box>
    );
  }
  
  if (error) return <Alert severity="error">Could not load subscription history. <Button onClick={onRefresh}>Retry</Button></Alert>;
  if (!subscriptionHistory || subscriptionHistory.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>No Subscription History</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You haven't created any subscriptions yet.
        </Typography>
        <Button 
          variant="outlined"
          onClick={onRefresh}
          sx={{ borderColor: '#29C0BB', color: '#29C0BB' }}
        >
          Refresh
        </Button>
      </Paper>
    );
  }
  
  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Subscription History
        </Typography>
        <Button 
          variant="outlined"
          onClick={onRefresh}
          sx={{ borderColor: '#29C0BB', color: '#29C0BB' }}
        >
          Refresh
        </Button>
      </Box>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {subscriptionHistory.map((subscription, index) => (
          <Card key={subscription.id || index} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Subscription Info */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {subscription.planName || 'Unknown Plan'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircleIcon 
                        sx={{ 
                          mr: 1, 
                          color: getStatusColor(subscription.status),
                          fontSize: 20 
                        }} 
                      />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: getStatusColor(subscription.status),
                          fontWeight: 'medium'
                        }}
                      >
                        {getStatusText(subscription.status)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Subscription ID: {subscription.id}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Created:
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(subscription.created)}
                      </Typography>
                    </Box>
                    
                    {subscription.current_period_start && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          Period Start:
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(subscription.current_period_start)}
                        </Typography>
                      </Box>
                    )}
                    
                    {subscription.current_period_end && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          Period End:
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(subscription.current_period_end)}
                        </Typography>
                      </Box>
                    )}
                    
                    {subscription.canceled_at && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          Cancelled:
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(subscription.canceled_at)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                
                {/* Payment Info */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Current plan details
                    </Typography>
                    
                    {subscription.amount != null && subscription.currency && (
                      <Typography variant="h5" color="primary" sx={{ mb: 1 }}>
                        {formatCurrency(subscription.amount, subscription.currency)}
                        <Typography component="span" variant="body2" color="text.secondary">
                          /{subscription.interval || 'month'}
                        </Typography>
                      </Typography>
                    )}
                  </Box>
                  
                  {subscription.paymentMethod && (
                    <Box sx={{ display: 'flex', alignItems: 'center', p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                      <CreditCardIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2">
                          {subscription.paymentMethod.brand?.charAt(0).toUpperCase() + subscription.paymentMethod.brand?.slice(1)} 
                          •••• {subscription.paymentMethod.last4}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Expires {subscription.paymentMethod.exp_month}/{subscription.paymentMethod.exp_year}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {subscription.discount && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: 'success.light', borderRadius: 1 }}>
                      <Typography variant="body2" color="success.dark">
                        Current discount: {subscription.discount.coupon?.name || 'Coupon'}
                        {subscription.discount.coupon?.percent_off && ` (${subscription.discount.coupon.percent_off}% off)`}
                        {subscription.discount.coupon?.amount_off && ` (${formatCurrency(subscription.discount.coupon.amount_off, subscription.currency)} off)`}
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
              <SubscriptionInvoices invoices={subscription.invoices} />
            </CardContent>
          </Card>
        ))}
      </Box>
    </Paper>
  );
};


export const SetupForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [confirmedMethodId, setConfirmedMethodId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const { setAlert } = useAlert();
  
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const result = confirmedMethodId ? { setupIntent: { status: 'succeeded', payment_method: confirmedMethodId } } : await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/settings',
        },
        redirect: 'if_required',
      });
      
      if (result.error) {
        throw new Error(result.error.message);
      }
      
      if (result.setupIntent?.status !== 'succeeded' || !result.setupIntent.payment_method) {
        throw new Error('Card setup is not complete. Please try again.');
      }
      const methodId = typeof result.setupIntent.payment_method === 'string'
        ? result.setupIntent.payment_method : result.setupIntent.payment_method.id;
      setConfirmedMethodId(methodId);
      await onSuccess(methodId);
    } catch (error) {
      setAlert('Failed to update payment method: ' + error.message, 'error');
    } finally {
      setIsProcessing(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement 
        options={{
          wallets: {
            applePay: 'never',
            googlePay: 'never',
            link: 'never'
          }
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button 
          type="button"
          variant="text"
          onClick={onCancel}
          disabled={isProcessing}
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          variant="contained"
          disabled={!stripe || isProcessing}
          sx={{ 
            backgroundColor: '#29C0BB', 
            '&:hover': { backgroundColor: '#1D8A87' } 
          }}
        >
          {isProcessing ? <CircularProgress size={24} color="inherit" /> : 'Save Payment Method'}
        </Button>
      </Box>
    </form>
  );
};

/* eslint-disable react/prop-types */
// Plan Selection Component for Upgrading
// Both entry points use the same presentation; checkout remains owned by this view.
export const PlanSelection = (props) => <PricingPlans {...props} />;

/* eslint-disable react/prop-types */
// Main Subscription View Component
const SubscriptionView = ({ tourTab = 'details' }) => {
  const [value, setValue] = useState(0);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionUnavailable, setSubscriptionUnavailable] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const { userDetails } = useSelector((state) => ({userDetails: state.globalState.userDetails}), shallowEqual);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const [hasUsedFreePlan, setHasUsedFreePlan] = useState(true);
  const [isRefreshingSubscription, setIsRefreshingSubscription] = useState(false);
  const [lastNextBillingDate, setLastNextBillingDate] = useState(null);
  const { setAlert } = useAlert();
  const subscriptionRequest = useRef(null);
  const subscriptionGeneration = useRef(0);
  const { refreshWallet } = useWalletBalance();
  useEffect(() => {
    setSelectedUpgradePlan(null);
    setClientSecret('');
    setUpgrading(false);
  }, [userDetails?.id]);
  useEffect(() => {
    setValue(tourTab === 'usage' ? 1 : tourTab === 'connections' ? 4 : 0);
  }, [tourTab]);

  const refreshingOverlay = isRefreshingSubscription ? (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: 'rgba(255,255,255,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1500,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2,
          bgcolor: '#fff',
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Loader />
        <Typography sx={{ ml: 2 }}>Updating subscription…</Typography>
      </Box>
    </Box>
  ) : null;
  
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const fetchSubscriptionInfo = useCallback((silent = false) => {
    if (!userDetails?.id) return Promise.resolve(null);
    if (subscriptionRequest.current?.userId === userDetails.id) return subscriptionRequest.current.promise;
    const generation = subscriptionGeneration.current;
    if (!silent) setLoading(true);
    setIsCheckingSubscription(true);
    const request = (async () => {
      try {
        const [data, response] = await Promise.all([
          loadRechargeSubscription(supabase, userDetails.id),
          authedFetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/billing-status`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
          }),
        ]);
        if (!response.ok) {
          const failure = await response.json().catch(() => ({}));
          const needsSupport = ['billing_environment_mismatch', 'billing_profile_unverified', 'billing_profile_ambiguous',
            'customer_not_owned', 'subscription_not_owned'].includes(failure.code);
          throw new Error(needsSupport
            ? 'Your billing records need confirmation. Please contact support before subscribing. You have not been charged by this check.'
            : 'Could not confirm your current subscription. Please try again.');
        }
        const fresh = reconcileBillingStatus(data, await response.json());
        if (generation !== subscriptionGeneration.current) return null;
        setHasUsedFreePlan(fresh.trial.status !== 'none');
        setSubscription(fresh);
        setSubscriptionUnavailable(false);
        setSubscriptionError(null);
        setLastNextBillingDate(fresh.next_billing_date || null);
        return fresh;
      } catch (error) {
        if (generation === subscriptionGeneration.current) {
          setSubscription(null);
          setSubscriptionUnavailable(true);
          setSubscriptionError(error.message.startsWith('Your billing records') ? error.message : null);
        }
        return null;
      } finally {
        if (generation === subscriptionGeneration.current) {
          setLoading(false);
          setIsCheckingSubscription(false);
          subscriptionRequest.current = null;
        }
      }
    })();
    subscriptionRequest.current = { userId: userDetails.id, promise: request };
    return request;
  }, [userDetails?.id]);

  useEffect(() => {
    fetchSubscriptionInfo();
    const stopWatchingReturn = onDocumentReturn(() => fetchSubscriptionInfo(true));
    return () => {
      subscriptionGeneration.current += 1;
      subscriptionRequest.current = null;
      stopWatchingReturn();
    };
  }, [fetchSubscriptionInfo]);

  const waitForSubscriptionUpdate = async (previousNextBillingDate) => {
    setIsRefreshingSubscription(true);
    try {
      for (let attempt = 0; attempt < MAX_REFRESH_ATTEMPTS; attempt++) {
        const fresh = await fetchSubscriptionInfo(true);
        const freshNextBillingDate = fresh?.next_billing_date || null;
        if (fresh && freshNextBillingDate !== previousNextBillingDate) {
          setAlert('Subscription updated successfully', 'success');
          return true;
        }
        await new Promise((resolve) => setTimeout(resolve, REFRESH_INTERVAL_MS));
      }
      return false;
    } finally {
      setIsRefreshingSubscription(false);
    }
  };

  const fetchSubscriptionHistory = async () => {
    if (!userDetails) return;
    
    setHistoryLoading(true);
    try {
      const response = await authedFetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/get-subscription-history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch subscription history');
      }
      
      const data = await response.json();
      setSubscriptionHistory(data.subscriptions || []);
      setHistoryError(false);
    } catch (error) {
      setHistoryError(true);
      console.error('Error fetching subscription history:', error);
      setAlert('Failed to load subscription history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Update the useEffect to also fetch subscription history when value changes to tab 3:
  useEffect(() => {
    if (value === 3 && userDetails) {
      fetchSubscriptionHistory();
    }
  }, [value, userDetails]);

  const handleUpgrade = async () => {
    const fresh = await fetchSubscriptionInfo();
    if (fresh) setUpgrading(true);
  };
  
  const handlePlanSelected = async (plan, cadence = 'monthly') => {
    const generation = subscriptionGeneration.current;
    // Carry the chosen billing cadence with the plan (D-022); the constants
    // stay immutable.
    plan = { ...plan, cadence };
    // Only create a SetupIntent for collecting a new payment method
    if (plan.name === 'Free') {
      setSelectedUpgradePlan(plan);
      return;
    }

    // For paid plans, create setup intent
    try {
      const response = await authedFetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/create-setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: userDetails.name,
          email: userDetails.email,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to setup payment method');
      }
      
      const { clientSecret } = await response.json();
      if (generation !== subscriptionGeneration.current) return;
      setClientSecret(clientSecret);
      setSelectedUpgradePlan(plan);
    } catch (error) {
      if (generation === subscriptionGeneration.current) {
        setAlert('Failed to setup payment method: ' + error.message, 'error');
      }
    }
  };
  
  const handleSubscriptionComplete = async ({ shouldPoll = true } = {}) => {
    setUpgrading(false);
    setClientSecret('');
    setSelectedUpgradePlan(null);

    const previousNextBillingDate = lastNextBillingDate || null;

    if (shouldPoll) {
      const updated = await waitForSubscriptionUpdate(previousNextBillingDate);

      if (!updated) {
        setAlert('Subscription is processing. Its status will be checked automatically when you return to this page.', 'warning');
        await fetchSubscriptionInfo();
      }
    } else {
      await fetchSubscriptionInfo();
    }
    await refreshWallet();
    setValue(0);
  };
  
  if (upgrading) {
    return (
      <>
        {refreshingOverlay}
        <Container>
        <Box sx={{ mb: 3 }}>
          <Button 
            variant="text" 
            onClick={() => { setUpgrading(false); setClientSecret(''); setSelectedUpgradePlan(null); }}
            sx={{ mb: 2 }}
          >
            Back to Subscription
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
            Upgrade Your Subscription
          </Typography>
        </Box>
        
        {selectedUpgradePlan ? (
          <Box sx={{ mx: 'auto' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Complete Your {selectedUpgradePlan.name} Plan Subscription
            </Typography>
            {selectedUpgradePlan.name === 'Free' ? (
              <FreeUpgradeForm
                plan={selectedUpgradePlan}
                onSuccess={handleSubscriptionComplete}
                onCancel={() => {
                  setSelectedUpgradePlan(null);
                }}
                subscription={subscription}
              />
            ) : (
              <StripeElements options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: { colorPrimary: '#29C0BB' }
                },
                paymentMethodCreation: 'manual'
              }}>
                <UpgradeForm
                  plan={selectedUpgradePlan}
                  onSuccess={handleSubscriptionComplete}
                  onCancel={() => {
                    setClientSecret('');
                    setSelectedUpgradePlan(null);
                  }}
                  userDetails={userDetails}
                  subscription={subscription}
                  onRefresh={() => fetchSubscriptionInfo(true)}
                  onManagePayment={() => { setUpgrading(false); setValue(2); }}
                />
              </StripeElements>
            )
          }
          </Box>
        ) : (
          <PlanSelection
            currentPlan={(subscription?.plan_name === 'free' && !hasUsedFreePlan) || ['canceled', 'incomplete_expired'].includes(subscription?.billing?.status) ? null : subscription?.billing?.tier || subscription?.plan_name}
            commitmentLocked={subscription?.billing?.commitment?.locked}
            billingUnavailable={subscriptionUnavailable || !subscription?.billing?.verified}
            currentCadence={subscription?.billing?.commitment?.cadence ||
              subscriptionHistory.find(
                (s) => s.status === 'active' || s.status === 'trialing'
              )?.cadence || 'monthly'
            }
            onPlanSelected={handlePlanSelected}
            showFreePlan={!hasUsedFreePlan}
          />
        )}
      </Container>
      </>
    );
  }
  
  return (
    <>
    {refreshingOverlay}
    <Container>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <PaymentIcon sx={{ fontSize: 30, color: '#29C0BB', mr: 1.5 }} />
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Subscription & Billing
        </Typography>
      </Box>

      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={value} 
            onChange={handleChange} 
            aria-label="subscription tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            TabIndicatorProps={{
              style: { backgroundColor: '#29C0BB' }
            }}
            sx={{
              '& .MuiTabs-scrollButtons': {
                color: '#29C0BB',
              },
              '& .MuiTabs-scrollButtons.Mui-disabled': {
                opacity: 0.3,
              },
            }}
          >
            <StyledTab 
              data-tour="subscription-details"
              label="Subscription Details" 
              icon={<PaymentIcon />} 
              iconPosition="start" 
              {...a11yProps(0)} 
            />
            <StyledTab 
              data-tour="subscription-usage"
              label="Usage Statistics" 
              icon={<TimelineIcon />} 
              iconPosition="start" 
              {...a11yProps(1)} 
            />
            <StyledTab 
              label="Payment Methods" 
              icon={<CreditCardIcon />} 
              iconPosition="start" 
              {...a11yProps(2)} 
            />
            <StyledTab
              label="Subscription History"
              icon={<PaymentIcon />}
              iconPosition="start"
              {...a11yProps(3)}
            />
            <StyledTab
              data-tour="subscription-client-connections"
              label="Client Connections"
              icon={<KeyIcon />}
              iconPosition="start"
              {...a11yProps(4)}
            />
          </Tabs>
        </Box>
        <TabPanel value={value} index={0}>
          <WalletBalanceSummary key={userDetails?.id || 'anonymous'} subscription={subscription} subscriptionLoading={loading || isCheckingSubscription || isRefreshingSubscription} subscriptionUnavailable={subscriptionUnavailable} onViewPlans={handleUpgrade} />
          <CurrentSubscription 
            subscription={subscription} 
            onUpgrade={handleUpgrade}
            loading={loading}
            handleSubscriptionComplete={handleSubscriptionComplete}
            unavailable={subscriptionUnavailable}
            onManagePayment={() => setValue(2)}
            errorMessage={subscriptionError}
            onRefresh={() => fetchSubscriptionInfo()}
          />
        </TabPanel>
        <TabPanel value={value} index={1}>
          <UsageStatistics 
            subscription={subscription}
            loading={loading}
          />
        </TabPanel>
        <TabPanel value={value} index={2}>
          {selectedUpgradePlan && <Alert severity="info" sx={{ mb: 2 }}>
            Add or select your payment card, then return to review the plan charges. Saving a card does not confirm a plan change or resume automatic renewal.
            <Button disabled={isRefreshingSubscription || isCheckingSubscription} onClick={async () => {
              const fresh = await fetchSubscriptionInfo(true);
              if (fresh) setUpgrading(true);
            }}>Return to {selectedUpgradePlan.name} plan change</Button>
          </Alert>}
          <PaymentMethods 
            userDetails={userDetails}
            loading={loading}
            onUpdated={() => fetchSubscriptionInfo(true)}
          />
        </TabPanel>
        <TabPanel value={value} index={3}>
          <SubscriptionHistory
            userDetails={userDetails}
            loading={historyLoading}
            subscriptionHistory={subscriptionHistory}
            error={historyError}
            onRefresh={fetchSubscriptionHistory}
          />
        </TabPanel>
        <TabPanel value={value} index={4}>
          <AccessCodesSection userDetails={userDetails} />
        </TabPanel>
      </Box>
    </Container>
    </>
  );
};


// Moving to the Free plan from Settings. Per decision D-007 a paid -> free
// move is a DOWNGRADE, not a signup: it must never call activate_free_plan()
// (which would either mint a second trial grant or be refused as
// `paid_plan_active`). Instead the client asks the subscription backend to
// cancel the Stripe subscription; the plan transition itself is performed
// server-side when the `customer.subscription.deleted` webhook arrives at the
// end of the paid period. Nothing about the plan is written from the browser.
//
// Retained Stripe IDs may be history. Verify billing status before choosing
// cancellation versus the server-authoritative one-time Free activation RPC.
export const FreeUpgradeForm = ({ plan, onSuccess, onCancel, subscription }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { setAlert } = useAlert();

  const action = freePlanAction(subscription);
  const isDowngrade = action.kind === 'cancel';
  const endDate = subscription?.billing?.period_end || subscription?.subscription_end_date;
  const periodEnd = endDate
    ? moment(endDate).format('MMM DD, YYYY')
    : null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (action.kind === 'blocked') { setAlert(action.message, 'warning'); return; }
    setIsProcessing(true);

    try {
      if (isDowngrade) {
        const response = await authedFetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/cancel-subscription`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            subscriptionId: action.id,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to schedule the switch to the Free plan');
        }

        setAlert(
          periodEnd
            ? `Your paid plan will not renew. You keep it and your remaining credits until ${periodEnd}, then the Free plan takes over automatically.`
            : 'Your paid plan will not renew. You keep it and your remaining credits until the end of the current billing period, then the Free plan takes over automatically.',
          'success'
        );
        onSuccess({ shouldPoll: false });
        return;
      }

      const outcome = await activateFreePlan(plan);
      setAlert(outcome.message, outcome.severity);
      // `settled` covers already_granted / paid_plan_active as well as a real
      // grant: in every one of those the account is provisioned, so refresh
      // and close rather than leaving the user on a form that cannot progress.
      if (outcome.settled) {
        onSuccess({ shouldPoll: false });
      }
    } catch (error) {
      setAlert(error.message, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mb: 3 }}>
        {action.kind === 'blocked' ? <Alert severity="warning">{action.message}</Alert> : isDowngrade ? (
          <>
            <Typography variant="body1" paragraph>
              You're about to switch to the Free plan. <strong>The change takes effect at the
              end of your current billing period{periodEnd ? ` (${periodEnd})` : ''}</strong>, not
              today.
            </Typography>
            <ul>
              <li>
                <Typography variant="body2">
                  You keep your current plan and its remaining credits until then.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  No further charges will be made.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  After that date your account continues on the Free plan. Free credits are a
                  one-time trial grant, so if you have already used it no new credits are added.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Any unused monthly plan credits expire when the period ends.
                </Typography>
              </li>
            </ul>
          </>
        ) : (
          <>
            <Typography variant="body1" paragraph>
              You're about to activate your Free plan. This includes:
            </Typography>
            <ul>
              {featuresForCadence(plan, plan.cadence).map((feature, index) => (
                <li key={index}><Typography variant="body2">{feature}</Typography></li>
              ))}
            </ul>
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          type="button"
          variant="text"
          onClick={onCancel}
          disabled={isProcessing}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isProcessing || action.kind === 'blocked'}
          sx={{
            backgroundColor: '#29C0BB',
            '&:hover': { backgroundColor: '#1D8A87' }
          }}
        >
          {isProcessing
            ? <CircularProgress size={24} color="inherit" />
            : (isDowngrade ? 'Switch to Free at period end' : `Activate ${plan.name} Plan`)}
        </Button>
      </Box>
    </form>
  );
};

/* eslint-disable react/prop-types */
// Upgrade Form Component
export const UpgradeForm = ({ plan, onSuccess, onCancel, userDetails, subscription, onRefresh, onManagePayment }) => {
  const stripe = useStripe();
  const elements = useElements();
  // Price in the smallest currency unit, from the shared plan constants.
  const originalPrice = plan.priceCents;

  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  const [isLoadingMethods, setIsLoadingMethods] = useState(true);

  const [subscriptionChangeDialogOpen, setSubscriptionChangeDialogOpen] = useState(false);
  const [planQuote, setPlanQuote] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [previewErrorCode, setPreviewErrorCode] = useState('');
  const [isPreviewing, setIsPreviewing] = useState(false);
  const previewGeneration = useRef(0);
  const isPlanChange = paidPlanAction(subscription).kind === 'change';
  // A status refresh creates a new object even when billing is unchanged.
  // Invalidate quotes on value changes, not on that new object reference.
  const billingSnapshot = JSON.stringify(subscription?.billing);
  useEffect(() => {
    previewGeneration.current += 1;
    setPlanQuote(null);
    setIsPreviewing(false);
    couponRequestGeneration.current += 1;
    setIsValidatingCoupon(false);
    setCouponValid(false);
    setCouponDiscount(null);
    setCouponMessage('');
    setPreviewError('Your billing details changed. Please retry the preview.');
    setPreviewErrorCode('');
    return () => { previewGeneration.current += 1; };
  }, [plan.tier, plan.cadence, billingSnapshot, userDetails?.id]);

  const [couponCode, setCouponCode] = useState('');
  const [couponMessage, setCouponMessage] = useState('');
  const [couponValid, setCouponValid] = useState(false);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(null);
  const { setAlert } = useAlert();

  // Every edit and every new request bumps this generation counter; a response
  // is applied only if it is still the newest. Without it a reply for `SAVE50`
  // can land after the field has already become `SAVE50X`, re-enabling the old
  // discount against the new code — the exact display/submit divergence the
  // reset below exists to prevent, just inside a ~300ms window.
  const couponRequestGeneration = useRef(0);

  // Editing the code invalidates the previous validation. The submitted body
  // now carries the raw `couponCode` rather than the `couponId` returned by
  // validate-coupon, so a stale `couponValid` would let the displayed total
  // (from the old coupon) disagree with the code actually sent.
  const handleCouponCodeChange = (event) => {
    setCouponCode(event.target.value);
    setCouponValid(false);
    setCouponDiscount(null);
    setCouponMessage('');
    // Supersede any validation still in flight.
    couponRequestGeneration.current += 1;
    setIsValidatingCoupon(false);
    previewGeneration.current += 1;
    setPlanQuote(null);
    setIsPreviewing(false);
    setPreviewError('The coupon changed. Review the updated charges before confirming.');
  };

  // Fetch saved payment methods
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await authedFetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/get-payment-methods`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch payment methods');
        }
        
        const data = await response.json();
        setPaymentMethods(data.paymentMethods || []);
        
        // If there are payment methods, select the first one by default
        if (data.paymentMethods && data.paymentMethods.length > 0) {
          setSelectedPaymentMethod(data.paymentMethods[0].id);
          setShowNewCardForm(false);
        } else {
          // If no payment methods, we need to create a setup intent
          // This should be initiated by the parent component now
          setShowNewCardForm(true);
        }
      } catch (error) {
        setAlert('Failed to load payment methods', 'error');
        setShowNewCardForm(true); // Fallback to new card form
      } finally {
        setIsLoadingMethods(false);
      }
    };
    
    fetchPaymentMethods();
  }, [setAlert]);

  const handlePaymentMethodChange = (event) => {
    const value = event.target.value;
    if (value === 'new') {
      setShowNewCardForm(true);
      setSelectedPaymentMethod(null);
    } else {
      setShowNewCardForm(false);
      setSelectedPaymentMethod(value);
    }
  };

  const calculateFinalPrice = () => {
    if (!couponValid || !couponDiscount) return originalPrice;
    
    if (couponDiscount.amountOff) {
      return Math.max(0, originalPrice - couponDiscount.amountOff);
    } else if (couponDiscount.percentOff) {
      return Math.max(0, originalPrice - (originalPrice * couponDiscount.percentOff / 100));
    }
    return originalPrice;
  };

  const validatePlanQuote = (quote, requestedCoupon) => {
    if (!quote || !Number.isSafeInteger(quote.amountDue) || quote.amountDue < 0 ||
        !/^[a-z]{3}$/.test(quote.currency) || typeof quote.effectiveAt !== 'string' || !moment(quote.effectiveAt).isValid() ||
        (quote.nextInvoiceAmount != null && (!Number.isSafeInteger(quote.nextInvoiceAmount) || quote.nextInvoiceAmount < 0)) ||
        !['immediate', 'period_end'].includes(quote.effective) || quote.tier !== plan.tier ||
        quote.cadence !== (plan.cadence || 'monthly') || quote.subscriptionId !== subscription.billing.id) {
      throw new Error('Could not confirm plan charges. Please retry.');
    }
    if (requestedCoupon && (!quote.coupon ||
        quote.coupon.code?.toLowerCase() !== requestedCoupon.toLowerCase() ||
        !['once', 'repeating', 'forever'].includes(quote.coupon.duration))) {
      throw new Error('Could not confirm the selected coupon. Please retry the preview.');
    }
    if (requestedCoupon && !((Number.isFinite(quote.coupon.percentOff) && quote.coupon.percentOff > 0 && quote.coupon.percentOff <= 100) ||
        (Number.isSafeInteger(quote.coupon.amountOff) && quote.coupon.amountOff > 0 && /^[a-z]{3}$/.test(quote.coupon.currency)))) {
      throw new Error('Could not confirm the selected coupon. Please retry the preview.');
    }
  };

  const validateCoupon = async () => {
    const requestedCode = couponCode.trim();
    if (!requestedCode) return;

    const generation = (couponRequestGeneration.current += 1);
    const quoteGeneration = ++previewGeneration.current;
    const isStale = () => couponRequestGeneration.current !== generation || previewGeneration.current !== quoteGeneration;

    setIsValidatingCoupon(true);
    setPlanQuote(null);
    setIsPreviewing(false);
    setCouponMessage('');
    setCouponValid(false);

    try {
      const response = isPlanChange
        ? await authedFetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/change-plan`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tier: plan.tier, cadence: plan.cadence || 'monthly', preview: true, couponCode: requestedCode }),
        })
        : await fetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/validate-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          couponCode: requestedCode,
        }),
      });

      const result = await response.json();
      const selectedCoupon = result.quote?.coupon;
      const data = isPlanChange ? { ...result, valid: !!selectedCoupon,
        description: selectedCoupon?.name, amountOff: selectedCoupon?.amountOff,
        percentOff: selectedCoupon?.percentOff } : result;

      // The field moved on while we were waiting: this answer is about a code
      // the user is no longer submitting, so it must not touch the total.
      if (isStale()) return;

      if (!response.ok) {
        throw new Error(data.error || 'Failed to validate coupon');
      }
      
      if (isPlanChange && (!selectedCoupon || selectedCoupon.code?.toLowerCase() !== requestedCode.toLowerCase())) {
        throw new Error('Could not confirm the selected coupon. Please retry.');
      }
      if (isPlanChange) {
        validatePlanQuote(result.quote, requestedCode);
        setPlanQuote(result.quote);
      }
      if (data.valid) {
        setCouponValid(true);
        setCouponDiscount({
          amountOff: data.amountOff,
          percentOff: data.percentOff
        });
        setCouponMessage(`${data.description}: ${data.amountOff ? `$${data.amountOff/100} off` : `${data.percentOff}% off`}`);
      } else {
        setCouponMessage(data.message || 'Invalid coupon');
      }
    } catch (error) {
      if (!isStale()) setCouponMessage(error.message);
    } finally {
      // A newer request (or an edit) owns the spinner from here on.
      if (!isStale()) setIsValidatingCoupon(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    const action = paidPlanAction(subscription);
    if (action.kind === 'blocked') { setAlert(action.message, 'warning'); return; }
    // Confirm an in-place change to an existing subscription.
    if (subscription && 
        hasExistingBillingSubscription(subscription) &&
        subscription.plan_name) {
      await previewPlanChange();
      return;
    }
    await processSubscription();
  };

  
  // A caller who already has a live paid subscription must go to /api/change-plan,
  // not /api/create-subscription -- the backend returns 409 subscription_already_active
  // for a second subscription, and change-plan modifies the existing one in place
  // (tier changes: immediate; same-tier cadence changes: at period end). The
  // existing subscription already has a payment method, so none is collected here.
  const previewPlanChange = async () => {
    if (couponCode.trim() && (!couponValid || isValidatingCoupon)) {
      setAlert('Apply the coupon or clear it before reviewing the plan change.', 'warning');
      return;
    }
    const requestedCoupon = couponValid ? couponCode.trim() : '';
    const generation = ++previewGeneration.current;
    setSubscriptionChangeDialogOpen(true);
    setPlanQuote(null); setPreviewError(''); setPreviewErrorCode(''); setIsPreviewing(true);
    try {
      const response = await authedFetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/change-plan`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier: plan.tier, cadence: plan.cadence || 'monthly', preview: true, ...(requestedCoupon ? { couponCode: requestedCoupon } : {}) }),
      });
      const data = await response.json();
      if (!response.ok) {
        const error = new Error(data.error || 'Could not confirm plan charges');
        error.code = data.code;
        throw error;
      }
      const quote = data.quote;
      validatePlanQuote(quote, requestedCoupon);
      if (generation === previewGeneration.current) setPlanQuote(quote);
    } catch (error) {
      if (generation === previewGeneration.current) {
        setPreviewError(error.message);
        setPreviewErrorCode(error.code || '');
      }
    } finally {
      if (generation === previewGeneration.current) setIsPreviewing(false);
    }
  };

  const processPlanChange = async () => {
    if (!planQuote || isPreviewing || isProcessing) return;
    setIsProcessing(true);
    try {
      const response = await authedFetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/change-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // cadence rides along (D-022); omitting it would still keep the
        // subscription's current cadence server-side.
        body: JSON.stringify({ tier: plan.tier, cadence: plan.cadence || 'monthly', quote: planQuote, resumeAutoRenewal: true, ...(planQuote.coupon ? { couponCode: planQuote.coupon.code } : {}) }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success !== true) {
        const error = new Error(data.error || 'Failed to change your plan');
        error.code = data.code;
        throw error;
      }

      if (data.changed === false) {
        setAlert(`You are already on the ${plan.name} plan.`, 'info');
      } else if (data.effective === 'period_end') {
        const when = data.effectiveAt
          ? moment(data.effectiveAt).format('MMM DD, YYYY')
          : 'the end of your current billing period';
        setAlert(
          `Your plan will change to ${plan.name} on ${when}. Your current allotment stays available until then.`,
          'success',
        );
      } else {
        setAlert(
          data.upgrade ? `You are now on the ${plan.name} plan. Subscription credits update after payment is confirmed.`
            : `You are now on the ${plan.name} plan. Your existing credits and their expiry are unchanged.`,
          'success',
        );
      }
      setSubscriptionChangeDialogOpen(false);
      onSuccess();
    } catch (error) {
      setPlanQuote(null); setPreviewError(error.message);
      setPreviewErrorCode(error.code || '');
      setAlert(error.message, 'error');
      // Stripe may have accepted part of the request. Re-read authoritative
      // billing rather than displaying success or assuming a rollback.
      try { await onRefresh?.(); } catch (_) { /* Keep the original error visible. */ }
    } finally {
      setIsProcessing(false);
    }
  };

  const processSubscription = async () => {    
    // Route an existing paid subscriber to the in-place change-plan path.
    const action = paidPlanAction(subscription);
    if (action.kind === 'blocked') { setAlert(action.message, 'warning'); return; }
    if (action.kind === 'change') {
      await processPlanChange();
      return;
    }

    if (!stripe) {
      return;
    }

    setIsProcessing(true);
    
    try {
      let result;
      // The backend derives the user, the Stripe customer and the Stripe price
      // from the verified session + tier. Never send userId/customerId/priceId.
      let paymentData = {
        tier: plan.tier,
        cadence: plan.cadence || 'monthly',
        couponCode: couponValid ? couponCode.trim() : null,
      };

      // If using a saved payment method
      if (selectedPaymentMethod && !showNewCardForm) {
        // Add existing payment method ID to request
        paymentData.paymentMethodId = selectedPaymentMethod;
      } 
      else if (elements) {
        const { error: submitError } = await elements.submit();
        if (submitError) {
          throw new Error(submitError.message);
        }
        // First collect the payment method
        const {error: paymentMethodError, paymentMethod} = await stripe.createPaymentMethod({
          elements,
          params: {
            billing_details: {
              name: userDetails.name,
              email: userDetails.email,
            },
          },
        });
        
        if (paymentMethodError) {
          throw new Error(paymentMethodError.message);
        }
        
        // Add new payment method ID to request
        paymentData.paymentMethodId = paymentMethod.id;
      } else {
        throw new Error('Either a payment method must be selected or card details must be provided');
      }
      
      // Send request to unified endpoint
      const response = await authedFetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/create-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(paymentData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        let errorMessage = errorData.error || 'Failed to process subscription';
        if (errorMessage.includes(':')) {
          errorMessage = errorMessage.split(':').pop().trim();
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      
      // Handle additional authentication if needed
      if (data.requiresAction) {
        result = await stripe.confirmCardPayment(data.clientSecret);
        if (result.error) {
          throw new Error(result.error.message);
        }
      }
      // No client-side `cancel-previous-subscriptions` call. A plan change is
      // an in-place modification of the existing Stripe subscription, done
      // server-side by /api/create-subscription. Cancelling from the browser
      // after the fact raced the webhooks and could expire credits that the
      // replacement subscription had just granted.
      onSuccess({ shouldPoll: true });
    } catch (error) {
      setAlert(error.message, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubscriptionChangeConfirm = async () => {
    await processPlanChange();
  };

  const handleSubscriptionChangeCancel = () => {
    if (isProcessing) return;
    previewGeneration.current += 1;
    setIsPreviewing(false); setPlanQuote(null);
    setSubscriptionChangeDialogOpen(false);
  };
  
  if (isLoadingMethods) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Loader />
      </Box>
    );
  }
  
  return (
    <>
    <form onSubmit={handleSubmit}>
      {!isPlanChange && paymentMethods.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Select Payment Method
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            {paymentMethods.map((method) => (
              <Box 
                key={method.id} 
                sx={{ 
                  border: `1px solid ${selectedPaymentMethod === method.id ? '#29C0BB' : '#e0e0e0'}`,
                  borderRadius: 1,
                  p: 2,
                  mb: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: selectedPaymentMethod === method.id ? 'rgba(41, 192, 187, 0.1)' : 'transparent'
                }}
                onClick={() => {
                  setSelectedPaymentMethod(method.id);
                  setShowNewCardForm(false);
                }}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value={method.id}
                  checked={selectedPaymentMethod === method.id}
                  onChange={handlePaymentMethodChange}
                  style={{ marginRight: 10 }}
                />
                <CreditCardIcon sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="body1">
                    {method.card.brand.charAt(0).toUpperCase() + method.card.brand.slice(1)} •••• {method.card.last4}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Expires {method.card.exp_month}/{method.card.exp_year}
                  </Typography>
                </Box>
              </Box>
            ))}
            
            <Box 
              sx={{ 
                border: `1px solid ${showNewCardForm ? '#29C0BB' : '#e0e0e0'}`,
                borderRadius: 1,
                p: 2,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: showNewCardForm ? 'rgba(41, 192, 187, 0.1)' : 'transparent'
              }}
              onClick={() => {
                setSelectedPaymentMethod(null);
                setShowNewCardForm(true);
              }}
            >
              <input
                type="radio"
                name="paymentMethod"
                value="new"
                checked={showNewCardForm}
                onChange={handlePaymentMethodChange}
                style={{ marginRight: 10 }}
              />
              <CreditCardIcon sx={{ mr: 1 }} />
              <Typography variant="body1">
                Use a new card
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      
      {!isPlanChange && showNewCardForm && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {paymentMethods.length > 0 ? 'Add New Card' : 'Enter Payment Details'}
          </Typography>
          <PaymentElement 
            options={{
              wallets: {
                applePay: 'never',
                googlePay: 'never',
                link: 'never'
              }
            }}
          />
        </Box>
      )}

      <Box sx={{ mt: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Have a coupon?
        </Typography>
        <TextField
          label="Coupon Code"
          variant="outlined"
          fullWidth
          value={couponCode}
          disabled={isProcessing}
          onChange={handleCouponCodeChange}
          sx={{ mb: 1 }}
        />
        {couponMessage && (
          <Typography 
            variant="body2" 
            color={couponValid ? "success.main" : "error.main"}
            sx={{ mt: 1 }}
          >
            {couponMessage}
          </Typography>
        )}
        <Button
          variant="text"
          onClick={validateCoupon}
          disabled={!couponCode || isValidatingCoupon || isProcessing}
          sx={{ color: '#29C0BB' }}
        >
          {isValidatingCoupon ? <CircularProgress size={20} /> : 'Apply Coupon'}
        </Button>
        {isPlanChange && <Typography variant="body2" color="text.secondary">Apply a code or leave this blank. Discounted charges and any replacement of an existing discount are shown in the plan-change preview.</Typography>}
      </Box>

      <Box component="section" aria-label="Order Summary" sx={{ mt: 3, mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Order Summary
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body1">{plan.name} Plan</Typography>
          <Typography variant="body1">${(originalPrice / 100).toFixed(2)} HKD</Typography>
        </Box>
        
        {!isPlanChange && couponValid && couponDiscount && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, color: 'success.main' }}>
            <Typography variant="body2">Coupon Discount</Typography>
            <Typography variant="body2">
              -${couponDiscount.amountOff ? 
                (couponDiscount.amountOff / 100).toFixed(2) : 
                ((originalPrice * couponDiscount.percentOff / 100) / 100).toFixed(2)} HKD
            </Typography>
          </Box>
        )}
        
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight="bold">{isPlanChange ? 'Plan price / month' : 'Total'}</Typography>
          <Typography variant="h6" fontWeight="bold" color="primary">
            ${((isPlanChange ? originalPrice : calculateFinalPrice()) / 100).toFixed(2)} HKD
          </Typography>
        </Box>
        {isPlanChange && planQuote && <Box aria-live="polite" sx={{ mt: 2 }}>
          {planQuote.coupon && <Typography color="success.main">
            Coupon applied: {planQuote.coupon.code} — {planQuote.coupon.percentOff != null
              ? `${planQuote.coupon.percentOff}% off`
              : `${(planQuote.coupon.amountOff / 100).toFixed(2)} ${planQuote.coupon.currency.toUpperCase()} off`}
          </Typography>}
          <Typography fontWeight="bold">Due now (Stripe preview): {(planQuote.amountDue / 100).toFixed(2)} {planQuote.currency.toUpperCase()}</Typography>
          {planQuote.nextInvoiceAmount != null && <Typography fontWeight="bold">
            Next invoice (Stripe preview): {(planQuote.nextInvoiceAmount / 100).toFixed(2)} {planQuote.currency.toUpperCase()}
          </Typography>}
          <Typography variant="body2" color="text.secondary">
            Previewed amounts include applicable discounts. Review the plan change to confirm the effective date and charges.
          </Typography>
        </Box>}
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button 
          type="button"
          variant="text"
          onClick={onCancel}
          disabled={isProcessing}
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          variant="contained"
          disabled={(!isPlanChange && ((!stripe && showNewCardForm) || (!selectedPaymentMethod && !showNewCardForm))) || isProcessing || isPreviewing || (isPlanChange && (isValidatingCoupon || (!!couponCode.trim() && !couponValid)))}
          sx={{ 
            backgroundColor: '#29C0BB', 
            '&:hover': { backgroundColor: '#1D8A87' } 
          }}
        >
          {isProcessing ? <CircularProgress size={24} color="inherit" /> : isPlanChange ? `Review ${plan.name} Plan change` : `Subscribe to ${plan.name} Plan`}
        </Button>
      </Box>
    </form>

    <Dialog
      open={subscriptionChangeDialogOpen}
      onClose={handleSubscriptionChangeCancel}
      aria-labelledby="subscription-change-dialog-title"
      aria-describedby="subscription-change-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="subscription-change-dialog-title" sx={{ color: '#f57c00', fontWeight: 'bold' }}>
        ⚠️ Change Subscription Plan
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="subscription-change-dialog-description" sx={{ mb: 2 }}>
          You are about to change from your current <strong>
            {subscription?.plan_name?.charAt(0).toUpperCase() + subscription?.plan_name?.slice(1)} Plan
          </strong> to the <strong>{plan.name} Plan</strong>.
        </DialogContentText>
        {isPreviewing && <Typography role="status">Checking the effective date and charges…</Typography>}
        {previewError && <Alert severity="error" sx={{ mb: 2 }}>{previewError}
          {previewErrorCode === 'payment_method_required' && onManagePayment &&
            <Button onClick={onManagePayment} disabled={isProcessing || isPreviewing}>Add or select payment card</Button>}
          <Button onClick={previewPlanChange} disabled={isProcessing || isPreviewing}>Retry preview</Button>
        </Alert>}
        {planQuote && <Box sx={{ p: 2, backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7', mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#856404' }}>
            ⚠️ Important Changes:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#856404' }}>
            <li>
              <Typography variant="body2">
                Your existing subscription is <strong>updated in place</strong> — it is not
                cancelled and re-created
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Effective date: {moment(planQuote.effectiveAt).format('MMM DD, YYYY HH:mm')}
                {planQuote.effective === 'immediate' ? ' (on confirmation)' : ' (end of the current billing period)'}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Amount due now: {(planQuote.amountDue / 100).toFixed(2)} {planQuote.currency.toUpperCase()}
                {planQuote.upgrade ? ' (prorated by Stripe)' : ' — no refund or additional charge for this change.'}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {planQuote.upgrade
                  ? 'Subscription credits are issued after the paid invoice is confirmed. Repeated changes do not issue the same plan allotment twice in one billing period.'
                  : 'Your existing credits and their expiry dates stay unchanged. No new subscription credits are issued now; the new plan’s normal credits are issued at renewal after payment.'}
              </Typography>
            </li>
            {planQuote.coupon && <li>
              <Typography variant="body2">Coupon: {planQuote.coupon.name} ({planQuote.coupon.code}).
                {' '}{planQuote.coupon.duration === 'once' ? 'Applies to one eligible invoice.' :
                  planQuote.coupon.duration === 'repeating' ? `Applies during the configured ${planQuote.coupon.durationInMonths}-month period.` :
                  'Applies to eligible renewals until removed.'}
                {' '}{planQuote.effective === 'period_end' ? 'The coupon starts when the scheduled plan takes effect.' :
                  'Stripe determines the eligible invoice; a one-time discount may apply at renewal.'}
              </Typography>
              {planQuote.coupon.replacesExistingDiscount && <Typography variant="body2" fontWeight="bold">
                This coupon replaces your existing subscription discount. Discounts are not combined.
              </Typography>}
            </li>}
            <li>
              <Typography variant="body2">
                Purchased (non-plan) credits are not affected
              </Typography>
            </li>
            {planQuote.nextInvoiceAmount !== null && planQuote.nextInvoiceAmount !== undefined && <li>
              <Typography variant="body2">Previewed next invoice: {(planQuote.nextInvoiceAmount / 100).toFixed(2)} {planQuote.currency.toUpperCase()}.
                Future taxes, discounts or account changes can affect the final invoice.</Typography>
            </li>}
            <li><Typography variant="body2">Changing plans will resume automatic renewal if it is currently off.
              Your subscription will renew on {moment(subscription.billing?.period_end).format('MMM DD, YYYY')}.</Typography></li>
          </ul>
        </Box>}
        
        <Box sx={{ p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #c8e6c9' }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#2e7d32' }}>
            ✅ What you'll get with {plan.name} Plan:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#2e7d32' }}>
            {featuresForCadence(plan, plan.cadence).map((feature, index) => (
              <li key={index}>
                <Typography variant="body2">{feature}</Typography>
              </li>
            ))}
          </ul>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Are you sure you want to proceed with this subscription change?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          onClick={handleSubscriptionChangeCancel} 
          variant="outlined"
          sx={{ borderColor: '#29C0BB', color: '#29C0BB' }}
          disabled={isProcessing}
        >
          Keep Current Plan
        </Button>
        <Button 
          onClick={handleSubscriptionChangeConfirm} 
          variant="contained"
          color="warning"
          disabled={isProcessing || isPreviewing || !planQuote || !!previewError}
          sx={{ ml: 2, backgroundColor: '#f57c00', '&:hover': { backgroundColor: '#ef6c00' } }}
        >
          {isProcessing ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Processing...
            </>
          ) : (
            `Yes, Change to ${plan.name} Plan`
          )}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default SubscriptionView;
